<template>
  <div
    v-if="!isRippleAdmin && developerMode == false"
    class="bxi-dashboard"
    :class="{ 'is-fullscreen': isFullscreen }"
  >
    <base-header type="pattern" class="pb-6 pb-8 pt-5 pt-md-8">
      <!-- Page heading -->
      <div class="page-heading mb-4">
        <div class="page-heading-text">
          <h1 class="page-title">Dashboard</h1>
          <p class="page-subtitle">Today's sales, orders and terminal activity at a glance.</p>
        </div>
        <div class="page-heading-actions">
          <a-select v-model="autoRefreshMinutes" style="width: 180px" @change="onAutoRefreshChange">
            <a-select-option v-for="opt in autoRefreshOptions" :key="opt" :value="opt">
              Refresh every {{ opt }} min
            </a-select-option>
          </a-select>
          <a-button class="fullscreen-toggle" @click="toggleFullscreen">
            <i :class="isFullscreen ? 'fas fa-compress' : 'fas fa-expand'" aria-hidden="true"></i>
            {{ isFullscreen ? 'Exit full screen' : 'Full screen' }}
          </a-button>
        </div>
      </div>

      <!-- KPI cards -->
      <div class="kpi-grid mb-4">
        <a-spin :spinning="spinner.sales">
          <div class="kpi-card">
            <div class="kpi-icon kpi-icon-green"><i class="fas fa-shopping-cart" aria-hidden="true"></i></div>
            <div class="kpi-body">
              <span class="kpi-label">Total Sales</span>
              <span class="kpi-value">{{ dashboard.total_sales }}</span>
            </div>
          </div>
        </a-spin>

        <a-spin :spinning="spinner.sales">
          <div class="kpi-card">
            <div class="kpi-icon kpi-icon-red"><i class="fas fa-undo" aria-hidden="true"></i></div>
            <div class="kpi-body">
              <span class="kpi-label">Total Returns</span>
              <span class="kpi-value">{{ dashboard.total_return }}</span>
            </div>
          </div>
        </a-spin>

        <a-spin :spinning="spinner.orders">
          <div class="kpi-card">
            <div class="kpi-icon kpi-icon-blue"><i class="fas fa-clipboard-check" aria-hidden="true"></i></div>
            <div class="kpi-body">
              <span class="kpi-label">Fulfilled Orders</span>
              <span class="kpi-value">{{ dashboard.total_fulfilled }}</span>
            </div>
          </div>
        </a-spin>

        <a-spin :spinning="spinner.orders">
          <div class="kpi-card">
            <div class="kpi-icon kpi-icon-orange"><i class="fas fa-clipboard-list" aria-hidden="true"></i></div>
            <div class="kpi-body">
              <span class="kpi-label">Pending Orders</span>
              <span class="kpi-value">{{ dashboard.total_pending }}</span>
            </div>
          </div>
        </a-spin>

        <a-spin :spinning="spinner.terminals">
          <div class="kpi-card">
            <div class="kpi-icon kpi-icon-teal"><i class="fas fa-desktop" aria-hidden="true"></i></div>
            <div class="kpi-body">
              <span class="kpi-label">Active Terminals</span>
              <span class="kpi-value">{{ dashboard.total_terminals }}</span>
            </div>
          </div>
        </a-spin>
      </div>

      <!-- Charts / tables -->
      <div class="table-grid">
        <!-- Invoice Processing -->
        <div class="dash-card is-clickable" @click="redirectUser('invoices')">
          <div class="dash-card-header">
            <div>
              <h2 class="dash-card-title">Invoice Processing</h2>
              <span v-if="pieData > 0" class="dash-card-subtitle">
                {{ invoiceStatus.completedStats + invoiceStatus.errorStats }} / {{ pieData }} processed · {{ invoiceStatus.errorStats }} error(s)
              </span>
            </div>
          </div>
          <div class="dash-card-body">
            <a-spin :spinning="spinner.invoices">
              <div v-if="pieData > 0" class="chart-wrap chart-wrap-sm">
                <canvas id="myChart"></canvas>
              </div>
              <a-empty v-else />
            </a-spin>
          </div>
        </div>

        <!-- Profile Syncing -->
        <div class="dash-card is-clickable" @click="redirectUser('syncs')">
          <div class="dash-card-header">
            <div>
              <h2 class="dash-card-title">Profile Syncing</h2>
              <span v-if="serverType == 'branch'" class="dash-card-subtitle">{{ syncCount.profilesCount }} / {{ syncCount.profilesTotal }} profiles synced</span>
              <span v-else class="dash-card-subtitle">{{ syncCount.siteCount }} / {{ syncCount.siteTotal }} sites synced</span>
            </div>
          </div>
          <div class="dash-card-body">
            <div class="table-responsive">
              <VueTabulator ref="tabulator" v-model="syncData" :options="syncOptions" />
            </div>
          </div>
        </div>
      </div>
    </base-header>
  </div>
  <div v-else class="bxi-dashboard">
    <base-header type="gradient-blue" class="pb-6 pb-8 pt-5 pt-md-8">
      <!-- Page heading -->
      <div class="page-heading mb-4">
        <h1 class="page-title">Admin Dashboard</h1>
        <p class="page-subtitle">Platform-wide accounts, sites, terminals and license activity.</p>
      </div>

      <!-- Totals -->
      <div class="kpi-grid mb-4">
        <a-spin :spinning="spinner.accounts">
          <div class="kpi-card">
            <div class="kpi-icon kpi-icon-green"><i class="fas fa-user" aria-hidden="true"></i></div>
            <div class="kpi-body">
              <span class="kpi-label">Total Accounts</span>
              <span class="kpi-value">{{ dashboard.total_accounts }}</span>
            </div>
          </div>
        </a-spin>

        <a-spin :spinning="spinner.sites">
          <div class="kpi-card">
            <div class="kpi-icon kpi-icon-red"><i class="fas fa-location-dot" aria-hidden="true"></i></div>
            <div class="kpi-body">
              <span class="kpi-label">Total Sites</span>
              <span class="kpi-value kpi-value-sm">{{ dashboard.total_sites }}</span>
            </div>
          </div>
        </a-spin>

        <a-spin :spinning="spinner.terminal_count">
          <div class="kpi-card">
            <div class="kpi-icon kpi-icon-blue"><i class="fas fa-desktop" aria-hidden="true"></i></div>
            <div class="kpi-body">
              <span class="kpi-label">Total Terminals</span>
              <span class="kpi-value">{{ dashboard.terminal_count }}</span>
            </div>
          </div>
        </a-spin>

        <a-spin :spinning="spinner.pending_invoices">
          <div class="kpi-card is-clickable" @click="redirectUser('invoices')">
            <div class="kpi-icon kpi-icon-indigo"><i class="fas fa-receipt" aria-hidden="true"></i></div>
            <div class="kpi-body">
              <span class="kpi-label">Pending Invoices</span>
              <span class="kpi-value">{{ dashboard.pending_invoices }}</span>
            </div>
          </div>
        </a-spin>
      </div>

      <!-- Top 5 account lists -->
      <div class="top5-grid mb-4">
        <div class="dash-card">
          <div class="dash-card-header">
            <h2 class="dash-card-title"><i class="fas fa-users top5-head-icon" aria-hidden="true"></i> Top Accounts — Users</h2>
          </div>
          <a-spin :spinning="spinner.top_accounts_by_user">
            <ol class="top5-list">
              <li v-for="(user, index) in topAccountsByUser" :key="index">
                <span class="top5-name">{{ user['Account Name'] }}</span>
                <span class="top5-count">{{ user['User Count'] }}</span>
              </li>
              <li v-if="!topAccountsByUser.length" class="top5-empty">No data available</li>
            </ol>
          </a-spin>
        </div>

        <div class="dash-card">
          <div class="dash-card-header">
            <h2 class="dash-card-title"><i class="fas fa-sitemap top5-head-icon" aria-hidden="true"></i> Top Accounts — Sites</h2>
          </div>
          <a-spin :spinning="spinner.top_accounts_by_site">
            <ol class="top5-list">
              <li v-for="(site, index) in topAccountsBySite" :key="index">
                <span class="top5-name">{{ site['Account Name'] }}</span>
                <span class="top5-count">{{ site['Site Count'] }}</span>
              </li>
              <li v-if="!topAccountsBySite.length" class="top5-empty">No data available</li>
            </ol>
          </a-spin>
        </div>

        <div class="dash-card">
          <div class="dash-card-header">
            <h2 class="dash-card-title"><i class="fas fa-computer top5-head-icon" aria-hidden="true"></i> Top Accounts — Terminals</h2>
          </div>
          <a-spin :spinning="spinner.top_accounts_by_terminal">
            <ol class="top5-list">
              <li v-for="(terminal, index) in topAccountsByTerminal" :key="index">
                <span class="top5-name">{{ terminal['Account Name'] }}</span>
                <span class="top5-count">{{ terminal['Terminal Count'] }}</span>
              </li>
              <li v-if="!topAccountsByTerminal.length" class="top5-empty">No data available</li>
            </ol>
          </a-spin>
        </div>
      </div>

      <!-- Expiring terminals -->
      <h3 class="section-title">Expiring Terminals</h3>
      <div class="kpi-grid">
        <a-spin :spinning="spinner.expiry_30_days">
          <div class="kpi-card is-clickable kpi-accent-green" @click="redirectUser('30')">
            <div class="kpi-body">
              <span class="kpi-label">Next 30 Days</span>
              <span class="kpi-value">{{ dashboard.expiry_30_days }}</span>
            </div>
          </div>
        </a-spin>

        <a-spin :spinning="spinner.expiry_60_days">
          <div class="kpi-card is-clickable kpi-accent-yellow" @click="redirectUser('60')">
            <div class="kpi-body">
              <span class="kpi-label">Next 60 Days</span>
              <span class="kpi-value">{{ dashboard.expiry_60_days }}</span>
            </div>
          </div>
        </a-spin>

        <a-spin :spinning="spinner.expiry_90_days">
          <div class="kpi-card is-clickable kpi-accent-red" @click="redirectUser('90')">
            <div class="kpi-body">
              <span class="kpi-label">Next 90 Days</span>
              <span class="kpi-value">{{ dashboard.expiry_90_days }}</span>
            </div>
          </div>
        </a-spin>

        <a-spin :spinning="spinner.expiry_365_days">
          <div class="kpi-card is-clickable kpi-accent-orange" @click="redirectUser('365')">
            <div class="kpi-body">
              <span class="kpi-label">Next 91–365 Days</span>
              <span class="kpi-value">{{ dashboard.expiry_365_days }}</span>
            </div>
          </div>
        </a-spin>
      </div>
    </base-header>
  </div>
</template>

<script>
  import secureStorage from '../controllers/secureStorage.js'
  import HTTPRequest from '../controllers/httpRequest.js'
  import moment from 'moment';
  import fullscreenToggle from '../mixins/fullscreenToggle'
  import autoRefresh from '../mixins/autoRefresh'

  var Chart = require('chart.js');
  // eslint-disable-next-line no-unused-vars
  var myChart
  var component_x
  import {
    mapState, mapActions
  } from 'vuex'

  export default {
    mixins: [fullscreenToggle, autoRefresh],
    computed: {
      ...mapState(['loggedIn', 'endPoint', 'serverType']),
    },
    data() {
      return {
        developerMode: secureStorage.getItem("developerMode"),
        site_type: 'ho',
        dashboard: {
          total_sales: "0",
          total_return: "0",
          total_fulfilled: "0",
          total_pending: "0",
          total_terminals: "0 / 0",
          terminal_count: "0",
          total_accounts: "0",
          total_sites: "HO: 0 | Branch: 0",
          top_accounts_by_site: "",
          top_accounts_by_user: "",
          top_accounts_by_terminal: "",
          pending_invoices:"0",
          expiry_365_days: "0",
          expiry_90_days: "0",
          expiry_60_days: "0",
          expiry_30_days: "0"

        },
        spinner: {
          sales: false,
          orders: false,
          terminals: false,
          invoices: false,
          accounts:false,
          sites:false,
          terminal_count: false,
          top_accounts_by_terminal: false,
          top_accounts_by_user:false,
          top_accounts_by_site: false,
          pending_invoices: false,
          terminal_license_expiry: false,
          expiry_365_days: false,
          expiry_90_days: false,
          expiry_60_days: false,
          expiry_30_days: false
        },
        topAccountsBySite: [],
        topAccountsByUser: [],
        topAccountsByTerminal: [],
        invoiceStatus: {
          completedStats: 0,
          unprocessedStats: 0,
          errorStats: 0
        },
        pieData: 1,
        chartOptions: {
          type: 'doughnut',
          data: {
            labels: ['Completed', 'Pending', 'Error'],
            datasets: [{
              label: '# of Invoices',
              data: [1, 1, 1],
              backgroundColor: [
                  '#2dce89',
                  '#fbb140',
                  '#eb5757'
              ],
              borderColor: [
                'rgba(255, 99, 132, 1)',
                'rgba(54, 162, 235, 1)',
                'rgba(255, 206, 86, 1)'
              ],
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false
          }
        },
        syncData: [],
        syncOptions: {
          layout:"fitDataStretch",
          pagination:"local",
          paginationSize:10,
          groupToggleElement:"header",
          groupBy:"site_name",
          groupHeader:this.groupHeader,
          groupStartOpen:false,
          columns:[
            {title: 'Synced', field: "is_latest", align:'center', formatter: this.is_latest_formatter, headerSort:false},
            {title: 'Profile', field: "profile", width:200, headerSort:false},
            {title: 'Profile Last Updated At', field: 'profile_last_updated_at', headerSort:false},
            {title: 'Site Delta Timestamp',  field: 'site_delta_timestamp', headerSort:false}
          ],
        },
        syncCount: {
          profilesCount: 0,
          profilesTotal: 0,
          siteCount: 0,
          siteTotal: 0
        },
        isRippleAdmin: secureStorage.getItem("isRippleAdmin")
      };
    },
    mounted: async function () {
      component_x = this

      if (this.loggedIn == false && this.developerMode == false) {
        this.$router.push('/')
      } else {
        await this.refreshDashboard();
        this.startAutoRefresh();
      }
    },
    methods: {
      ...mapActions(['updateCurrentProfile']),
      moment,

      // Fetches every KPI / chart on this dashboard. Called once on mount and
      // again on every auto-refresh tick (see mixins/autoRefresh.js).
      async refreshDashboard() {
        Object.assign(component_x.spinner, {
          sales: true,
          orders: true,
          terminals: true,
          invoices: true,
          syncs: true,
          accounts: true,
          sites:true,
          terminal_count: true,
          top_accounts_by_terminal: true,
          top_accounts_by_user:true,
          top_accounts_by_site: true,
          pending_invoices: true,
          terminal_license_expiry: true,
          expiry_365_days: true,
          expiry_90_days: true,
          expiry_60_days: true,
          expiry_30_days: true
        })
        if (this.isRippleAdmin){
          let account_data = []
        
        account_data = await HTTPRequest.getRequest(component_x.endPoint + 'accounts/total_account_counts', null)
        
        if (account_data != undefined) {
          let total_accounts = component_x.formatNumber(Number(account_data.total_accounts))
          Object.assign(component_x.dashboard, {
            total_accounts: `${total_accounts}`
          })
        }
        component_x.spinner.accounts = false

        let expiry_terminal_365_days = []
        
        expiry_terminal_365_days = await HTTPRequest.getRequest(component_x.endPoint + 'accounts/expiry_365_days', null)
        
        if (account_data != undefined) {
          let expiry_terminal_365 = component_x.formatNumber(Number(expiry_terminal_365_days.expiry_365[0]['Terminal Count']))
          Object.assign(component_x.dashboard, {
            expiry_365_days: `${expiry_terminal_365}`
          })
        }
        component_x.spinner.expiry_365_days = false

        let expiry_terminal_90_days = []
        
        expiry_terminal_90_days = await HTTPRequest.getRequest(component_x.endPoint + 'accounts/expiry_90_days', null)
        
        if (expiry_terminal_90_days != undefined) {
          let expiry_terminal_90 = component_x.formatNumber(Number(expiry_terminal_90_days.expiry_90[0]['Terminal Count']))
          Object.assign(component_x.dashboard, {
            expiry_90_days: `${expiry_terminal_90}`
          })
        }
        component_x.spinner.expiry_90_days = false

        let expiry_terminal_60_days = []
        
        expiry_terminal_60_days = await HTTPRequest.getRequest(component_x.endPoint + 'accounts/expiry_60_days', null)
        
        if (expiry_terminal_60_days != undefined) {
          let expiry_terminal_60 = component_x.formatNumber(Number(expiry_terminal_60_days.expiry_60[0]['Terminal Count']))
          Object.assign(component_x.dashboard, {
            expiry_60_days: `${expiry_terminal_60}`
          })
        }
        component_x.spinner.expiry_60_days = false

        let expiry_terminal_30_days = []
        
        expiry_terminal_30_days = await HTTPRequest.getRequest(component_x.endPoint + 'accounts/expiry_30_days', null)
        
        if (expiry_terminal_30_days != undefined) {
          let expiry_terminal_30 = component_x.formatNumber(Number(expiry_terminal_30_days.expiry_30[0]['Terminal Count']))
          Object.assign(component_x.dashboard, {
            expiry_30_days: `${expiry_terminal_30}`
          })
        }
        component_x.spinner.expiry_30_days = false

        let site_data = []
        
        site_data = await HTTPRequest.getRequest(component_x.endPoint + 'sites/total_site_count', null)
        
        if (site_data != undefined) {
          let site_count_ho = null;
          let site_count_branch = null;

          site_data.total_sites.forEach(site => {
            if (site.name === "HO") {
              site_count_ho = site.site_count;
            }
            if (site.name === "Branch") {
              site_count_branch = site.site_count;
            }
          });

          Object.assign(component_x.dashboard, {
            total_sites: `HO: ${site_count_ho} | Branch: ${site_count_branch}`
          });

          
        }
         component_x.spinner.sites = false

         let account_site_data = []
        
         account_site_data = await HTTPRequest.getRequest(component_x.endPoint + 'accounts/top_account_by_sites', null)
        
         if (account_site_data != undefined) {
            const topAccountsBySite = account_site_data.top_sites
              .sort((a, b) => b["Site Count"] - a["Site Count"])
              .slice(0, 5);

            Object.assign(component_x, {
              topAccountsBySite,
            });
          }

          component_x.spinner.top_accounts_by_site = false;

          let account_user_data = []
        
         account_user_data = await HTTPRequest.getRequest(component_x.endPoint + 'accounts/top_account_by_users', null)
        
         if (account_user_data != undefined) {
            const topAccountsByUser = account_user_data.top_users
              .sort((a, b) => b["User Count"] - a["User Count"])
              .slice(0, 5);

            Object.assign(component_x, {
              topAccountsByUser,
            });
          }

          component_x.spinner.top_accounts_by_user = false;

          let account_terminal_data = []
        
          account_terminal_data = await HTTPRequest.getRequest(component_x.endPoint + 'accounts/top_account_by_terminals', null)
       
        if (account_terminal_data != undefined) {
           const topAccountsByTerminal = account_terminal_data.top_terminals
             .sort((a, b) => b["Terminal Count"] - a["Terminal Count"])
             .slice(0, 5);

           Object.assign(component_x, {
            topAccountsByTerminal,
           });
         }

         component_x.spinner.top_accounts_by_terminal = false;

        let terminal_count_data = []
        
        terminal_count_data = await HTTPRequest.getRequest(component_x.endPoint + 'accounts/total_terminal_count', null)
        
        if (terminal_count_data != undefined) {
          let terminal_count = component_x.formatNumber(Number(terminal_count_data.terminal_count))
          Object.assign(component_x.dashboard, {
            terminal_count: `${terminal_count}`
          })
        }
        component_x.spinner.terminal_count = false

        let invoice_data = []
        
        invoice_data = await HTTPRequest.getRequest(component_x.endPoint + 'accounts/pending_invoices', null)
        
        if (invoice_data != undefined) {
          let unprocessed_invoices = invoice_data.unprocessed_invoices
          Object.assign(component_x.dashboard, {
            pending_invoices: `${unprocessed_invoices}`
          })
        }
        component_x.spinner.pending_invoices = false
        }
        let siteId = secureStorage.getItem("defaultSiteId")
        let site = await HTTPRequest.getRequest(this.endPoint + "/sites/" + siteId, null)
        if(site != undefined){
            component_x.site_type = site.site_type
        }

        let sales_data = []
        if(component_x.site_type == 'branch'){
          sales_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_documents/pos_document_summary?site_id=' + siteId, null)
        } else {
          sales_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_documents/pos_document_summary', null)
        }

        if(sales_data != undefined){
          let total_sales = component_x.convertToCurrency(Number(sales_data.total_sales))
          let total_returns = component_x.convertToCurrency(Number(sales_data.total_returns))

          Object.assign(component_x.dashboard, {
            total_sales: total_sales,
            total_return: total_returns,
          })
        }
        component_x.spinner.sales = false

        let order_data = []
        if(component_x.site_type == 'branch'){
          order_data = await HTTPRequest.getRequest(component_x.endPoint + 'documents/sales_order_summary?site_id=' + siteId, null)
        } else {
          order_data = await HTTPRequest.getRequest(component_x.endPoint + 'documents/sales_order_summary', null)
        }

        if(order_data != undefined){
          let total_fulfilled = component_x.formatNumber(Number(order_data.total_fulfilled))
          let total_pending = component_x.formatNumber(Number(order_data.total_pending))

          Object.assign(component_x.dashboard, {
            total_fulfilled: total_fulfilled,
            total_pending: total_pending,
          })
        }
        component_x.spinner.orders = false

        let terminal_data = []
        if(component_x.site_type == 'branch'){
          terminal_data = await HTTPRequest.getRequest(component_x.endPoint + 'terminals/terminal_summary?site_id=' + siteId, null)
        } else {
          terminal_data = await HTTPRequest.getRequest(component_x.endPoint + 'terminals/terminal_summary', null)
        }

        let session_data = []
        if (component_x.site_type == 'branch') {
          session_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_sessions?session_count=1&site_id=' + siteId + '&business_date=' + moment().format("YYYY-MM-DD"), null)
        } else {
          session_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_sessions?session_count=1&business_date=' + moment().format("YYYY-MM-DD"), null)
        }

        // STARTS Here
        if (terminal_data != undefined) {
          let total_sessions = component_x.formatNumber(Number(session_data.total_sessions))
          let total_terminals = component_x.formatNumber(Number(terminal_data.total_terminals))

          // Object.assign(component_x.dashboard, {
          //   total_terminals: component_x.site_type !== 'branch' ? `${total_sessions}` : `${total_sessions} / ${total_terminals}`
          // })
          Object.assign(component_x.dashboard, {
            total_terminals: `${total_sessions} / ${total_terminals}`
          })
        }
        component_x.spinner.terminals = false
        // ENDS here

        let stats = []
        if(component_x.site_type == 'branch'){
          stats = await HTTPRequest.getRequest(this.endPoint + "api_invoices/get_stats?business_date=" + moment().format("YYYY-MM-DD") + '&site_id=' + siteId, null)
        } else {
          stats = await HTTPRequest.getRequest(this.endPoint + "api_invoices/get_stats?business_date=" + moment().format("YYYY-MM-DD"), null)
        }

        if(stats != undefined){
          Object.assign(component_x.invoiceStatus, {
            completedStats: Number(stats.no_of_completed.length),
            unprocessedStats: Number(stats.no_of_unprocessed.length),
            errorStats: Number(stats.no_of_error.length)
          })

          component_x.pieData = component_x.invoiceStatus.completedStats + component_x.invoiceStatus.unprocessedStats + component_x.invoiceStatus.errorStats
          component_x.chartOptions.data.datasets[0].data = [component_x.invoiceStatus.completedStats,
            component_x.invoiceStatus.unprocessedStats,
            component_x.invoiceStatus.errorStats]

          // Destroy the previous chart first: this runs on every auto-refresh,
          // and Chart.js can't draw twice on the same canvas.
          if (myChart) {
            myChart.destroy();
          }
          myChart = new Chart("myChart", component_x.chartOptions);
        }
        component_x.spinner.invoices = false

        let syncs = []
        component_x.syncCount = {
          profilesCount: 0,
          profilesTotal: 0,
          siteCount: 0,
          siteTotal: 0
        }

        if(component_x.site_type == 'branch'){
          syncs = await HTTPRequest.getRequest(this.endPoint + 'branch_delta_sync_details?site_id=' + secureStorage.getItem("defaultSiteId"), null)
        } else {
          syncs = await HTTPRequest.getRequest(this.endPoint + 'branch_delta_sync_details', null)
        }

        component_x.syncData = syncs
        let unique = [];
        let distinct = [];
        for( let i = 0; i < component_x.syncData.length; i++ ){
          if( !unique[component_x.syncData[i].site_name]){
            distinct.push(component_x.syncData[i].site_name);
            let value = component_x.syncData[i].site_name
            let is_latest_count = component_x.syncData.filter(d => d.is_latest=="Yes" && d.site_name == value).length
            let site_count = component_x.syncData.filter(d => d.site_name == value).length
            if(is_latest_count == site_count){
              component_x.syncCount.siteCount += 1
            }
            Object.assign(this.syncCount, {
              profilesCount: component_x.syncCount.profilesCount + is_latest_count,
              profilesTotal: component_x.syncCount.profilesTotal + site_count
            })
            unique[component_x.syncData[i].site_name] = 1;
          }
        }
        component_x.syncCount.siteTotal = distinct.length
        component_x.spinner.syncs = false
      },
      formatNumber(num) {
        return num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
      },
      convertToCurrency(num){
        return num.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
      },
      redirectUser(type) {
        if (type == 'invoices') {
          this.setCurrentProfile('pos_sales_dashboards');
        } else if(type == 'syncs'){
          if(this.serverType == 'branch'){
            this.setCurrentProfile('terminal_delta_sync_details');
          } else {
            this.setCurrentProfile('branch_delta_sync_details');
          }
        }
        const expiry_cap = ['365', '90', '60', '30'];

        if (expiry_cap.includes(type)) {
          this.$router.push(`/expiry_date/${type}`);
        } else {
          this.$router.push('/tools');
        }
      },
      setCurrentProfile(profile) {
        this.updateCurrentProfile(profile);
      },
      // eslint-disable-next-line no-unused-vars
      is_latest_formatter: function(cell, formatterParams, onRendered){
        var is_latest_format = ''
        var is_latest = cell.getValue();

        if (is_latest == 'Yes')
          is_latest_format = "<span style='color: #00ff00;'><i class='fas fa-sync-alt'></i></span>"
        else
          is_latest_format = "<span style='color: #ff0000;'><i class='far fa-times-circle'></i></span>"

        return is_latest_format;
      },
      groupHeader:function(value, count, data, group){
        var is_latest_count = this.syncData.filter(d => d.is_latest=="Yes" && d.site_name == value).length
        var site_count = this.syncData.filter(d => d.site_name == value).length

        var group_sync_status = ''
        if (is_latest_count == site_count)
          group_sync_status = "<span style='color: #00ff00;'><i class='fas fa-sync-alt'></i></span>"
        else
          group_sync_status = "<span style='color: #ff0000;'><i class='fas fa-times-circle'></i></span>"

        var arrow_key = ''
        if (group._group.visible) {
          arrow_key = "<i class='fas fa-chevron-down'></i>"
        } else {
          arrow_key = "<i class='fas fa-chevron-right'></i>"
        }
        return arrow_key + group_sync_status + "  " + value + "<span style='color:#6468E3; margin-left:10px;'>" + is_latest_count + " out of "+ site_count + " profile(s) synced</span>";
      },
    }
  };
</script>
<style lang='scss'>
  @import "~vue-tabulator/dist/scss/bootstrap/tabulator_bootstrap";
  .tabulator-group-toggle {
    display: none;
  }
  .ant-spin-nested-loading,
  .ant-spin-container {
    height: 100%;
  }

  .bxi-dashboard {
    .page-heading {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;

      .page-title {
        color: #fff;
        font-weight: 700;
        font-size: 1.75rem;
        margin-bottom: 0.25rem;
      }
      .page-subtitle {
        color: rgba(255, 255, 255, 0.85);
        font-size: 1rem;
        margin-bottom: 0;
      }
      .page-heading-actions {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-shrink: 0;
      }
      .fullscreen-toggle i {
        margin-right: 6px;
      }
    }

    // Full screen: cover the sidebar / navbar. z-index stays below Ant
    // Design popups (1050) so dropdowns and date pickers still show on top.
    &.is-fullscreen {
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 1000;
      overflow-y: auto;
      background: #f8f9fe;
    }

    .section-title {
      color: #fff;
      font-size: 1.1rem;
      font-weight: 700;
      margin-bottom: 1rem;
    }

    // Responsive KPI card grid
    .kpi-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 1rem;
    }

    .kpi-card {
      display: flex;
      align-items: center;
      gap: 1rem;
      background: #fff;
      border-radius: 0.75rem;
      padding: 1.25rem;
      box-shadow: 0 0 2rem 0 rgba(136, 152, 170, 0.15);
      height: 100%;
      transition: transform 0.15s ease, box-shadow 0.15s ease;

      &.is-clickable {
        cursor: pointer;
        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 0.5rem 2rem 0 rgba(136, 152, 170, 0.3);
        }
      }

      .kpi-icon {
        flex: 0 0 auto;
        width: 3rem;
        height: 3rem;
        border-radius: 0.75rem;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        font-size: 1.1rem;
      }
      .kpi-icon-green  { background: linear-gradient(87deg, #2dce89, #2dcecc); }
      .kpi-icon-red    { background: linear-gradient(87deg, #f5365c, #f56036); }
      .kpi-icon-blue   { background: linear-gradient(87deg, #11cdef, #1171ef); }
      .kpi-icon-indigo { background: linear-gradient(87deg, #5e72e4, #825ee4); }
      .kpi-icon-orange { background: linear-gradient(87deg, #fb6340, #fbb140); }
      .kpi-icon-teal   { background: linear-gradient(87deg, #2dcecc, #11cdef); }

      .kpi-body {
        display: flex;
        flex-direction: column;
        min-width: 0;
      }
      .kpi-label {
        font-size: 0.7rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: #8898aa;
        margin-bottom: 0.25rem;
      }
      .kpi-value {
        font-size: 1.5rem;
        font-weight: 700;
        color: #32325d;
        line-height: 1.15;
      }
      .kpi-value-sm {
        font-size: 1.1rem;
      }

      // Accent cards (no icon) use a colored left border
      &.kpi-accent-green  { border-left: 4px solid #2dce89; }
      &.kpi-accent-yellow { border-left: 4px solid #ffd600; }
      &.kpi-accent-red    { border-left: 4px solid #f5365c; }
      &.kpi-accent-orange { border-left: 4px solid #fb6340; }
    }

    // Generic card used for charts, tables and lists
    .dash-card {
      background: #fff;
      border-radius: 0.75rem;
      box-shadow: 0 0 2rem 0 rgba(136, 152, 170, 0.15);
      overflow: hidden;
      transition: transform 0.15s ease, box-shadow 0.15s ease;

      &.is-clickable {
        cursor: pointer;
        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 0.5rem 2rem 0 rgba(136, 152, 170, 0.3);
        }
      }

      .dash-card-header {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        padding: 1.25rem 1.5rem;
        border-bottom: 1px solid #e9ecef;
      }
      .dash-card-title {
        font-size: 1.1rem;
        font-weight: 700;
        color: #32325d;
        margin: 0;
      }
      .dash-card-subtitle {
        display: block;
        font-size: 0.8rem;
        color: #8898aa;
        margin-top: 0.25rem;
      }
      .dash-card-body {
        padding: 1.5rem;
      }
    }

    .chart-wrap {
      position: relative;
      height: 320px;
    }
    .chart-wrap-sm {
      height: 260px;
    }

    // Chart + sync table two-up layout
    .table-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
      gap: 1.5rem;
    }

    // Top 5 account lists
    .top5-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
      gap: 1.5rem;
    }

    .top5-head-icon {
      color: #8898aa;
      margin-right: 0.4rem;
    }

    .top5-list {
      list-style: none;
      margin: 0;
      padding: 0.5rem 0;
      counter-reset: top5;

      li {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.75rem;
        padding: 0.65rem 1.5rem;
        border-bottom: 1px solid #f1f3f6;

        &:last-child { border-bottom: none; }

        &:not(.top5-empty)::before {
          counter-increment: top5;
          content: counter(top5);
          flex: 0 0 auto;
          width: 1.5rem;
          height: 1.5rem;
          border-radius: 50%;
          background: #f6f9fc;
          color: #8898aa;
          font-size: 0.75rem;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }
      .top5-name {
        flex: 1 1 auto;
        min-width: 0;
        font-size: 0.9rem;
        color: #32325d;
        font-weight: 600;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .top5-count {
        flex: 0 0 auto;
        font-size: 0.9rem;
        font-weight: 700;
        color: #5e72e4;
      }
      .top5-empty {
        color: #adb5bd;
        justify-content: center;
        padding: 1.5rem;
      }
    }
  }
  </style>
